// src/app/components/reporting/components/export-actions.tsx
// Install pptxgenjs first: npm install pptxgenjs
import { useState } from 'react';
import { FileText, Mail, Link2, Linkedin, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { createShareLink } from '@/app/services/reporting/insights-api-service';
import type { GeneratedReport } from '../types';

// ── Utilities ─────────────────────────────────────────────────────────────────

function copyText(text: string) {
  if (navigator.clipboard) { navigator.clipboard.writeText(text); return; }
  const el = document.createElement('textarea');
  el.value = text; document.body.appendChild(el); el.select();
  document.execCommand('copy'); document.body.removeChild(el);
}

// ── PowerPoint ────────────────────────────────────────────────────────────────

async function generatePptx(report: GeneratedReport) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod     = await import('pptxgenjs' as any);
  const pptxgen = mod.default ?? mod;
  const pres    = new pptxgen();
  pres.layout   = 'LAYOUT_WIDE';

  const TEAL = '00B8A9', DARK = '1A1A2E', GRAY = '6B7280', WHITE = 'FFFFFF', LIGHT = 'F0FAFA';

  const s1 = pres.addSlide();
  s1.background = { color: DARK };
  s1.addText(report.title, { x: 0.5, y: 1.2, w: '90%', fontSize: 36, bold: true, color: WHITE });
  s1.addText(`${report.format}  ·  Audience: ${report.audience}  ·  Confidence: ${report.confidence}%`,
    { x: 0.5, y: 2.6, w: '90%', fontSize: 14, color: TEAL });
  s1.addText(new Date(report.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    { x: 0.5, y: 3.1, w: '90%', fontSize: 13, color: GRAY });

  const s2 = pres.addSlide();
  s2.background = { color: WHITE };
  s2.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 0.9, fill: { color: DARK } });
  s2.addText('Executive Summary', { x: 0.5, y: 0.15, w: '90%', fontSize: 20, bold: true, color: WHITE });
  s2.addText(report.executive_summary, { x: 0.5, y: 1.2, w: '90%', h: 3.5, fontSize: 16, color: DARK, wrap: true, valign: 'top' });

  report.sections.forEach((section, idx) => {
    const s = pres.addSlide();
    s.background = { color: idx % 2 === 0 ? WHITE : LIGHT };
    s.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 0.9, fill: { color: TEAL } });
    s.addText(`${idx + 1}. ${section.title}`, { x: 0.5, y: 0.15, w: '85%', fontSize: 20, bold: true, color: WHITE });
    if (section.key_metric) s.addText(section.key_metric, { x: '88%', y: 0.12, w: 1.2, h: 0.6, fontSize: 18, bold: true, color: TEAL, align: 'right' });
    s.addText(section.content, { x: 0.5, y: 1.1, w: '90%', h: 2.8, fontSize: 15, color: DARK, wrap: true, valign: 'top' });
    if (section.reasoning) {
      s.addShape(pres.ShapeType.rect, { x: 0.5, y: 4.2, w: '90%', h: 0.7, fill: { color: 'EAF8F7' }, line: { color: TEAL, width: 1 } });
      s.addText(`AI: ${section.reasoning}`, { x: 0.7, y: 4.3, w: '88%', fontSize: 11, color: GRAY, italic: true });
    }
  });

  if (report.key_findings.length > 0) {
    const sf = pres.addSlide();
    sf.background = { color: DARK };
    sf.addText('Key Findings', { x: 0.5, y: 0.4, w: '90%', fontSize: 28, bold: true, color: WHITE });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sf.addText(report.key_findings.map((f) => ({ text: f, options: { bullet: true } })) as any,
      { x: 0.5, y: 1.4, w: '90%', h: 4, fontSize: 16, color: TEAL, valign: 'top' });
  }

  await pres.writeFile({ fileName: `${report.title.replace(/[^a-z0-9]/gi, '_')}.pptx` });
}

// ── Email ─────────────────────────────────────────────────────────────────────

function buildEmail(report: GeneratedReport): { subject: string; body: string } {
  const subject = `${report.title} — ${report.format}`;
  const secs    = report.sections.map((s, i) =>
    `${i + 1}. ${s.title}${s.key_metric ? ` (${s.key_metric})` : ''}\n${s.content}`
  ).join('\n\n');
  const findings = report.key_findings.map((f) => `• ${f}`).join('\n');
  const body = `${subject}\n${'─'.repeat(60)}\n\nEXECUTIVE SUMMARY\n${report.executive_summary}\n\n${'─'.repeat(60)}\nSECTIONS\n\n${secs}\n\n${'─'.repeat(60)}\nKEY FINDINGS\n\n${findings}\n\n${'─'.repeat(60)}\nFormat: ${report.format}  |  Audience: ${report.audience}  |  Confidence: ${report.confidence}%\nGenerated: ${new Date(report.created_at).toLocaleDateString()}\n`;
  return { subject, body };
}

// ── LinkedIn ──────────────────────────────────────────────────────────────────

function buildLinkedIn(report: GeneratedReport): string {
  const first   = report.key_findings[0] || report.title;
  const snippet = report.executive_summary.split('. ').slice(0, 2).join('. ').trim();
  const bullets = report.key_findings.slice(0, 4).map((f) => `✅ ${f}`).join('\n');
  const post    = `🚀 ${first}\n\n${snippet}\n\nKey highlights:\n${bullets}\n\n#BrandIntelligence #AIPerception #BrandStrategy #DataDriven`;
  return post.length > 1300 ? post.slice(0, 1297) + '...' : post;
}

// ── Copy Modal ────────────────────────────────────────────────────────────────

function CopyModal({ title, text, mailtoHref, onClose }: {
  title: string; text: string; mailtoHref?: string; onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full rounded-2xl overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elevation-lg)',
          maxWidth: 'min(42rem, calc(100dvw - 24px))',
          maxHeight: 'min(90dvh, 100dvh - 24px)',
        }}
      >
        <div
          className="flex items-center justify-between px-4 md:px-6 py-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h3
            style={{
              fontWeight: 'var(--font-weight-semibold)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            className="flex-shrink-0"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X className="w-5 h-5" style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <textarea
            readOnly
            value={text}
            rows={12}
            className="w-full p-3 md:p-4 rounded-xl resize-none"
            style={{
              backgroundColor: 'var(--muted)',
              border: '1px solid var(--border)',
              fontSize: 'var(--text-sm)',
              lineHeight: '1.6',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>
        {/* Footer — stacks on mobile so 3 buttons don't clip */}
        <div
          className="flex flex-col sm:flex-row items-stretch gap-3 px-4 md:px-6 py-4"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <button
            onClick={handleCopy}
            className="flex-1 py-2.5 rounded-lg flex items-center justify-center gap-2"
            style={{
              backgroundColor: copied ? 'var(--alpha-teal)' : 'var(--foreground)',
              color: 'white',
              fontWeight: 'var(--font-weight-semibold)',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
            }}
          >
            {copied ? <><Check className="w-4 h-4" /> Copied!</> : 'Copy to Clipboard'}
          </button>
          {mailtoHref && (
            <a
              href={mailtoHref}
              target="_blank"
              rel="noreferrer"
              className="flex-1 py-2.5 rounded-lg flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-semibold)',
                border: '1px solid var(--border)',
                textDecoration: 'none',
                fontSize: 'var(--text-sm)',
              }}
            >
              <Mail className="w-4 h-4" /> Open in Email
            </a>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg"
            style={{
              backgroundColor: 'var(--muted)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Export button ─────────────────────────────────────────────────────────────

type Status = 'idle' | 'loading' | 'done' | 'error';

function ExportButton({ icon, label, description, onClick, status }: {
  icon: React.ReactNode; label: string; description: string;
  onClick: () => void; status: Status;
}) {
  return (
    <button
      onClick={onClick}
      disabled={status === 'loading'}
      className="p-3 rounded-lg text-left transition-all hover:translate-y-[-1px] w-full"
      style={{
        backgroundColor: status === 'done' ? 'var(--alpha-teal-5)' : 'var(--background)',
        border: `1px solid ${
          status === 'done'
            ? 'var(--alpha-teal)'
            : status === 'error'
            ? 'var(--infrared-coral)'
            : 'var(--border)'
        }`,
        cursor: status === 'loading' ? 'wait' : 'pointer',
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--signal-blue-12)' }}
        >
          {status === 'loading'
            ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--signal-blue)' }} />
            : status === 'done'
            ? <Check className="w-4 h-4" style={{ color: 'var(--alpha-teal)' }} />
            : icon}
        </div>
        <div className="min-w-0 flex-1">
          <p
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              marginBottom: '2px',
            }}
          >
            {label}
          </p>
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {status === 'done' ? '✓ Done' : status === 'error' ? 'Failed — retry' : description}
          </p>
        </div>
      </div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ExportActions({ report }: { report: GeneratedReport }) {
  type ExportId = 'powerpoint' | 'email' | 'link' | 'linkedin';
  const [statuses, setStatuses] = useState<Record<ExportId, Status>>({
    powerpoint: 'idle', email: 'idle', link: 'idle', linkedin: 'idle',
  });
  const [modal, setModal] = useState<{ title: string; text: string; mailtoHref?: string } | null>(null);

  const set = (id: ExportId, s: Status) => setStatuses((p) => ({ ...p, [id]: s }));

  const handlePptx = async () => {
    set('powerpoint', 'loading');
    try {
      await generatePptx(report);
      set('powerpoint', 'done');
      toast.success('PowerPoint downloaded!');
      setTimeout(() => set('powerpoint', 'idle'), 3000);
    } catch {
      set('powerpoint', 'error');
      toast.error('PowerPoint failed. Run: npm install pptxgenjs');
      setTimeout(() => set('powerpoint', 'idle'), 4000);
    }
  };

  const handleEmail = () => {
    const { subject, body } = buildEmail(report);
    setModal({
      title: 'Email Summary',
      text: body,
      mailtoHref: `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    });
  };

  const handleLink = async () => {
    set('link', 'loading');
    try {
      const { url } = await createShareLink(report.id);
      copyText(url);
      set('link', 'done');
      toast.success('Shareable link copied!', { description: url, duration: 6000 });
      setTimeout(() => set('link', 'idle'), 4000);
    } catch {
      set('link', 'error');
      toast.error('Could not generate share link');
      setTimeout(() => set('link', 'idle'), 3000);
    }
  };

  const handleLinkedIn = () => setModal({ title: 'LinkedIn Post', text: buildLinkedIn(report) });

  return (
    <>
      {/* 1 col mobile, 2 cols sm, 4 cols md+ — per Gagan's call */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <ExportButton
          icon={<FileText className="w-4 h-4" style={{ color: 'var(--signal-blue)' }} />}
          label="PowerPoint"
          description="Download .pptx slides"
          onClick={handlePptx}
          status={statuses.powerpoint}
        />
        <ExportButton
          icon={<Mail className="w-4 h-4" style={{ color: 'var(--signal-blue)' }} />}
          label="Email Summary"
          description="Copy & open in email client"
          onClick={handleEmail}
          status={statuses.email}
        />
        <ExportButton
          icon={<Link2 className="w-4 h-4" style={{ color: 'var(--signal-blue)' }} />}
          label="Share Link"
          description="Copy public URL to clipboard"
          onClick={handleLink}
          status={statuses.link}
        />
        <ExportButton
          icon={<Linkedin className="w-4 h-4" style={{ color: 'var(--signal-blue)' }} />}
          label="LinkedIn Post"
          description="AI-formatted, ready to paste"
          onClick={handleLinkedIn}
          status={statuses.linkedin}
        />
      </div>
      {modal && (
        <CopyModal
          title={modal.title}
          text={modal.text}
          mailtoHref={modal.mailtoHref}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}